import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";

/**
 * alarmsDuck — SINGLE SOURCE OF TRUTH for all alarm data.
 *
 * Alarms arrive via SignalR (AlarmMessage event) and are dispatched here.
 * Uses createEntityAdapter for normalized O(1) lookups by composite key.
 * Components derive what they need via memoized selectors.
 *
 * Entity ID: `${alarmId}_${hwId}` (composite key)
 */

/** Normalize a raw SignalR alarm message into the internal format */
const normalizeAlarm = (item) => ({
  id: `${item.AlarmId ?? item.alarmId}_${item.HwId ?? item.hwId}`,
  alarmId: item.AlarmId ?? item.alarmId,
  hwId: item.HwId ?? item.hwId,
  displayName: item.ValueName ?? item.displayName,
  sourceTimestamp: item.SourceTimestamp ?? item.sourceTimestamp,
  triggerTimeStamp: item.TriggerTimeStamp ?? item.triggerTimeStamp,
  turnedOffTimeStamp: item.TurnedOffTimeStamp ?? item.turnedOffTimeStamp,
  isActive: (item.TurnedOffTimeStamp ?? item.turnedOffTimeStamp) === 0,
  severity: item.Severity ?? item.severity,
  category: item.Category ?? item.category,
  brief: item.Brief ?? item.brief,
  descriptionFI: item.DescriptionFI ?? item.descriptionFI,
  descriptionEN: item.DescriptionEN ?? item.descriptionEN,
});

const alarmsAdapter = createEntityAdapter();

export const alarmsDefaultState = alarmsAdapter.getInitialState({
  // Legacy fields kept for backward compatibility during migration
  entries: {},
  formattedEntries: {},
});

/** Rebuild the byHwId grouped index from the flat alarms array */
const rebuildByHwId = (allAlarms) => {
  const result = {};
  allAlarms.forEach((alarm) => {
    if (!result[alarm.hwId]) result[alarm.hwId] = [];
    result[alarm.hwId].push(alarm);
  });
  return result;
};

// Thunk functions
export const upsertAlarmsForMap = createAsyncThunk(
  "alarms/upsertAlarmsForMap",
  async (params, thunk) => {
    return { systemId: params.systemId, data: params.data };
  }
);

const alarmsSlice = createSlice({
  name: "alarms",
  initialState: alarmsDefaultState,
  reducers: {
    /** Upsert a single alarm from socket. Uses entity adapter for O(1) upsert by composite ID. */
    upsertAlarm(state, action) {
      const raw = action.payload.data;
      const alarm = normalizeAlarm(raw);
      alarmsAdapter.upsertOne(state, alarm);
      // Keep legacy entries in sync
      const allAlarms = Object.values(state.entities);
      state.entries = rebuildByHwId(allAlarms);
    },

    /** Remove a single alarm by composite ID */
    removeAlarm(state, action) {
      const { alarmId, hwId } = action.payload;
      const compositeId = `${alarmId}_${hwId}`;
      alarmsAdapter.removeOne(state, compositeId);
      const allAlarms = Object.values(state.entities);
      state.entries = rebuildByHwId(allAlarms);
    },

    /** Clear all inactive alarms (keep only active) */
    clearInactiveAlarms(state) {
      const inactiveIds = Object.values(state.entities)
        .filter((a) => !a.isActive)
        .map((a) => a.id);
      alarmsAdapter.removeMany(state, inactiveIds);
      const allAlarms = Object.values(state.entities);
      state.entries = rebuildByHwId(allAlarms);
    },

    /** Legacy: bulk-set alarm entries (used by old code paths during migration) */
    upsertAlarmEntries(state, action) {
      const { data } = action.payload;
      state.entries = data;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(upsertAlarmsForMap.fulfilled, (state, action) => {
      const { data } = action.payload;
      state.entries = data;
    });
  },
});

/** Entity adapter selectors — use getSelectors() to get selectAll, selectById, etc. */
export const alarmsAdapterSelectors = alarmsAdapter.getSelectors();

export const { upsertAlarm, removeAlarm, clearInactiveAlarms, upsertAlarmEntries } = alarmsSlice.actions;

export default alarmsSlice.reducer;
